import React from 'react';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { Icon } from 'components/common';
import { AUTO_AI_ACTIVITIES } from '../utils/scileadsAiActivities';

// Styled components
const ItemContainer = styled.div`
  line-height: 1.4;
  overflow: hidden;
  max-width: 350px;
`;

const ItemTitle = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;

const ItemMetadata = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #666;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ShowText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;

const ItemLabel = styled.span`
  display: flex;
  align-items: center;
  overflow: hidden;
`;

const LabelIcon = styled(Icon)`
  flex-shrink: 0;
  margin-right: 4px;
`;

const LabelText = styled.span`
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

// Constants moved from workItemUtils.js
export const categoryPropertyMap = {
  publication: ['articleTitle', 'citationId'],
  tradeshow: ['title', 'sciLeadsPosterId', 'show'],
  funding: ['title', 'sciLeadsAwardId'],
  trial: ['title', 'nctId'],
};

export const linkPropertyMap = {
  publication: ['publication', 'citationId'],
  tradeshow: ['poster', 'sciLeadsPosterId'],
  funding: ['project', 'sciLeadsAwardId'],
  trial: ['trial', 'nctId'],
};

export const iconPropertyMap = {
  publication: 'book',
  tradeshow: 'image',
  funding: 'award',
  trial: 'trial',
};

export const categoryNameMap = {
  publication: 'Choose Publication',
  tradeshow: 'Choose Poster',
  funding: 'Choose Funding',
  trial: 'Choose Trial',
};

const STATIC_ACTIVITY_KEYS = Object.values(AUTO_AI_ACTIVITIES).map(
  a => a.value
);

/**
 * Component that processes work items data and builds a tree structure
 * for use in the ActivitySelector component
 */
const WorkItemTree = {
  /**
   * Builds a tree structure for work items to be used in the selector component
   * @param {Object} workItemsResponse - Response from activity search API
   * @param {Object} selectedWorkItemLabels - Currently selected work item labels
   * @param {Function} setInitialWorkItem - Function to set initial work item when a specific ID is matched
   * @param {String} workItemId - ID of the work item to highlight initially (can be null)
   * @return {Array} Tree data for the selector component
   */
  buildWorkItemTreeData: (
    workItemsResponse,
    selectedWorkItemLabels,
    setInitialWorkItem,
    workItemId
  ) => {
    const updatedCategories = {};
    const topLevelNodes = [];

    Object.keys(selectedWorkItemLabels || {}).forEach(category => {
      if (!category || category === 'static') {
        // Only add static activities that are NOT already in topLevelNodes
        Object.values(selectedWorkItemLabels[category]).forEach(node => {
          if (!STATIC_ACTIVITY_KEYS.includes(node.value)) {
            topLevelNodes.push(node);
          }
        });
      }
    });

    if (workItemsResponse?.groupedResults?.length > 0) {
      workItemsResponse.groupedResults.forEach(l => {
        const category = l.category && l.category.toLowerCase();

        // If this is a static/uncategorized node, add as top-level and skip grouping
        if (!category || category === 'static') {
          if (!STATIC_ACTIVITY_KEYS.includes(l.value)) {
            topLevelNodes.push(l);
          }
          return;
        }

        const childProperties = categoryPropertyMap[category];
        const linkProperties = linkPropertyMap[category];

        if (!childProperties || !linkProperties) return;

        if (!updatedCategories[category]) {
          updatedCategories[category] = {
            key: category,
            title: categoryNameMap[category],
            value: category,
            selectable: false,
            checkable: false,
            icon: iconPropertyMap[category],
            children: [],
          };
        }

        const concatenatedValue = childProperties
          .map(property => l[property])
          .join(' ');

        const newNode = {
          key: concatenatedValue,
          title: l[childProperties[0]],
          displayTitle: (
            <ItemContainer>
              <ItemTitle>{l[childProperties[0]]}</ItemTitle>
              {l.date && (
                <ItemMetadata>
                  {l.date && (
                    <>
                      <Icon
                        icon='calendar'
                        size='1x'
                        style={{ fontSize: '12px', color: '#666' }}
                      />
                      <span>{dayjs(l.date).format('MMM D, YYYY')}</span>
                    </>
                  )}
                  {l.show && (
                    <>
                      <Icon
                        icon='image'
                        size='1x'
                        style={{ fontSize: '12px', color: '#666' }}
                      />
                      <ShowText>{l.show}</ShowText>
                    </>
                  )}
                </ItemMetadata>
              )}
            </ItemContainer>
          ),
          value: concatenatedValue,
          displayLinks: true,
          category: category,
          link: `/${linkProperties[0]}/${l[linkProperties[1]]}`,
          id: l[linkProperties[1]],
          label: (
            <ItemLabel key={l[childProperties[0]]}>
              <LabelIcon icon={iconPropertyMap[category]} size='1x' />
              <LabelText>{l[childProperties[0]]}</LabelText>
            </ItemLabel>
          ),
        };

        // Check if this work item should be selected initially
        const isSelected =
          workItemId && workItemId === l[linkProperties[1]] + '';
        if (isSelected && setInitialWorkItem) {
          setInitialWorkItem([[concatenatedValue], [newNode]]);
        }

        // Add the node to the category, using selected node if available
        updatedCategories[category].children.push(
          selectedWorkItemLabels[category]?.[concatenatedValue] || newNode
        );
      });
    }

    // 3. Add any selected items that might not be in the search results
    Object.keys(selectedWorkItemLabels || {}).forEach(category => {
      if (category && category !== 'static' && !updatedCategories[category]) {
        updatedCategories[category] = {
          key: category,
          title: category.charAt(0).toUpperCase() + category.slice(1),
          value: category,
          selectable: false,
          checkable: false,
          icon: iconPropertyMap[category],
          children: Object.values(selectedWorkItemLabels[category]),
        };
      } else if (category && updatedCategories[category]) {
        const existingKeys = new Set(
          updatedCategories[category].children.map(child => child.key)
        );
        Object.values(selectedWorkItemLabels[category]).forEach(child => {
          if (!existingKeys.has(child.key)) {
            updatedCategories[category].children.push(child);
          }
        });
      }
    });

    // 4. Return top-level static/uncategorized nodes first, then grouped categories
    return [...topLevelNodes, ...Object.values(updatedCategories)];
  },
};

export default WorkItemTree;
